import {Injectable, Injector, OnDestroy} from "@angular/core";
import {Subject} from "rxjs";
import {ISubject, UntilSubject} from "../../api/custom-subject-observable";
import {IVideoScreen, Media, WebCameraView} from "@models";
import {TimeProperties} from "../../modules/web-manager/subModules/scenario-archive/components/scenario-archive-base";
// Please do not update this import, if you import from services it will cause a circular dependency
import {HubEventEmitter} from "../../api/generated/hub/hub-event-emitter.service";

@Injectable({
	providedIn: "root",
})
export class GlobalEventEmitterService extends HubEventEmitter {
	constructor() {
		super();
	}

	//------------------------HUB

	public readonly onSystemHubDisconnected: ISubject<void> = new Subject();
	public readonly onSystemHubError: ISubject<{message: string}> = new Subject();
	public readonly onSystemHubReconnected: ISubject<{connectionId: string}> = new Subject();
	//This may only trigger when you login for the first time
	public readonly onSystemHubConnected: ISubject<{connectionId: string}> = new Subject();

	//------------------------AUDIO

	public readonly onAudioReconnectedSuccessfully: ISubject<void> = new Subject();
	public readonly onAudioReconnectionFailed: ISubject<void> = new Subject();
	public readonly onAudioNeedsUserConfirmation: ISubject<void> = new Subject();
	public readonly onAudioPlaybackNeedsSecureContext: ISubject<void> = new Subject();
	public readonly onAudioStateChanged: ISubject<{
		audioState: "slow-network" | "slow-cpu" | "stable";
	}> = new Subject();

	// ------------------------ CUSTOM ------------------------ //

	public readonly onTrackingMapRequestRefresh: ISubject<void> = new Subject();
	public readonly onDebriefingInitializationProgress: ISubject<{
		progress: number;
		message: string;
	}> = new Subject();
	public readonly onNetworkChanged: ISubject<void> = new Subject();
	public readonly onCameraScreenSwitched: ISubject<{
		camera: WebCameraView;
		position: number;
	}> = new Subject();

	public readonly onScenarioRecordingPlayerFinished: ISubject<void> = new Subject();
	public readonly onScenarioRecordingPlayerJumped: ISubject<void> = new Subject();
	public readonly onScenarioRecordingPlayerPaused: ISubject<void> = new Subject();
	public readonly onScenarioRecordingPlayerPlaying: ISubject<void> = new Subject();
	public readonly onScenarioRecordingPlayerStopped: ISubject<void> = new Subject();
	public readonly onScenarioRecordingPlayerTimeUpdated: ISubject<TimeProperties> = new Subject();
	public readonly onSelectedFullScreenChanged: ISubject<{
		currentFullScreen: IVideoScreen;
	}> = new Subject();
	public readonly onShowNavigation: ISubject<{visible: boolean}> = new Subject();
	public readonly onShowBurgerMenu: ISubject<{visible: boolean}> = new Subject();
	public readonly closeSideNavigation: ISubject<{visible: boolean}> = new Subject();
	public readonly onToggleShowTranslationKeys: ISubject<{visible: boolean}> = new Subject();
	public readonly onToggleShowPermissionTooltips: ISubject<{visible: boolean}> = new Subject();
	public readonly onTrackingMapRequestReset: ISubject<void> = new Subject();
	public readonly onUserPermChanged: ISubject<void> = new Subject();
	public readonly onMediaCurrentTimeChanged: ISubject<{
		media: Media;
		currentTime: number;
		totalTime: number;
	}> = new Subject();

	public readonly onDynamicAudioChannelsChanged: ISubject<void> = new Subject<void>();

	public readonly onNotifyExport: ISubject<{scenarioRecordingId: string}> = new Subject();
	public readonly onShowAbsoluteTimeChanged: ISubject<{value: boolean}> = new Subject();
}

@Injectable()
export class EventEmitterService extends GlobalEventEmitterService implements OnDestroy {
	protected $OnDestroy = new Subject<void>();

	constructor(injector: Injector) {
		super();

		let globalEmitter = injector.get(GlobalEventEmitterService);
		this.iterateSubjects((key) => {
			// @ts-ignore - This is a hack to check if the property is a Subject
			if (globalEmitter[key] instanceof Subject) {
				// @ts-ignore - This is a hack to set the property to a new UntilSubject
				this[key] = new UntilSubject(globalEmitter[key], this.$OnDestroy);
			}
		});
	}

	private iterateSubjects(func: (key: string) => void) {
		let keys = Reflect.ownKeys(this);
		for (let index in keys) {
			let key = keys[index].toString();
			if (key.toLowerCase().startsWith("on")) {
				func(key);
			}
		}
	}

	ngOnDestroy(): void {
		this.$OnDestroy.next();
		this.$OnDestroy.complete();
	}
}
